import { Base } from '@studiometa/js-toolkit';
import ArchivesGallerySwitcher from './ArchivesGallerySwitcher.js';
import ArchivesGalleryItem from './ArchivesGalleryItem.js';
import gsap from 'gsap';
import Observer from 'gsap/dist/Observer.min.js';
import { nextFrame } from '@studiometa/js-toolkit/utils';

gsap.registerPlugin(Observer);

/**
 * ArchivesGallery class.
 * @augments {Base}
 */
export default class ArchivesGallery extends Base {
  /**
   * Configuration statique de la classe.
   * @type {object}
   * @property {string} name - Nom du composant.
   * @property {string[]} refs - Références aux éléments DOM.
   * @property {string[]} emits - Événements émis.
   * @property {object} options - Options du composant.
   * @property {object} options.mode - Mode d'affichage de la galerie.
   * @property {object} components - Composants enfants.
   */
  static config = {
    name: 'ArchivesGallery',
    refs: ['container', 'overlay', 'loader', 'loadCounter', 'nav'],
    emits: ['mode', 'move'],
    options: {
      mode: { type: String, default: 'slider' },
    },
    components: {
      ArchivesGallerySwitcher,
      ArchivesGalleryItem,
    },
  };

  /**
   * Méthode appelée lorsque le composant est monté.
   * Charge toutes les images, initialise les animations et les écouteurs d'événements.
   * @returns {void}
   */
  mounted() {
    let imagesLoaded = 0;
    Promise.all(
      this.items.map((item) =>
        item.loadImage().then(() => {
          imagesLoaded += 1;
          gsap.to(this.$refs.loader, {
            scaleX: imagesLoaded / this.items.length,
            duration: 0.5,
            ease: 'power2.out',
          });
          this.$refs.loadCounter.textContent = `${Math.min(Math.round((imagesLoaded / this.items.length) * 100), 100)}%`;
        }),
      ),
    ).then(() => {
      this.$el.classList.remove('is-loading');
      this.$refs.loadCounter.textContent = '';
      gsap.to(this.$refs.loader, {
        scaleX: 1,
        duration: 0.5,
        ease: 'power2.out',
      });
      gsap.to(this.container, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out',
      });
      gsap.to(this.$refs.nav, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.out',
      });
    });

    this.resized();

    this.incrX = -this.halfX / 2;
    this.incrY = 0;

    gsap.set(this.container, {
      x: this.incrX,
      y: this.incrY,
      force3D: true,
    });

    Observer.create({
      target: window,
      type: 'wheel,touch,pointer',
      onChange: () => {
        this.$emit('move');
      },
      onChangeX: (self) => {
        if (self.event.type === 'wheel') this.incrX -= self.deltaX;
        else this.incrX += self.deltaX * 3;
        this.xAnimation = this.xTo(this.incrX);
      },
      onChangeY: (self) => {
        if (this.mode === 'slider') {
          if (self.event.type !== 'wheel') return;
          this.incrX -= self.deltaY;
          this.xAnimation = this.xTo(this.incrX);
          return;
        }
        if (self.event.type === 'wheel') this.incrY -= self.deltaY;
        else this.incrY += self.deltaY * 3;
        this.yAnimation = this.yTo(this.incrY);
      },
    });
  }

  /**
   * Recalcule les dimensions et réinitialise les fonctions d'animation après un redimensionnement.
   * @returns {void}
   */
  resized() {
    this.xAnimation?.kill();
    this.yAnimation?.kill();

    this.halfX = this.container.clientWidth / 2;
    this.wrapX = gsap.utils.wrap(-this.halfX, 0);

    this.halfY = this.container.clientHeight / 2;
    this.wrapY = gsap.utils.wrap(-this.halfY, 0);

    this.xTo = gsap.quickTo(this.container, 'x', {
      duration: 3,
      ease: 'power4',
      force3D: true,
      immediateRender: false,
      modifiers: {
        x: gsap.utils.unitize(this.wrapX),
      },
    });

    this.yTo = gsap.quickTo(this.container, 'y', {
      duration: 3,
      ease: 'power4',
      force3D: true,
      immediateRender: false,
      modifiers: {
        y: gsap.utils.unitize(this.wrapY),
      },
    });
  }

  /**
   * Getter pour récupérer tous les éléments de la galerie.
   * @returns {ArchivesGalleryItem[]} Liste des éléments de la galerie.
   */
  get items() {
    return this.$children.ArchivesGalleryItem ?? [];
  }

  /**
   * Z-index disponible pour les éléments actifs.
   * @type {number}
   */
  availableZIndex = 10;

  /**
   * Incrément de position X actuel.
   * @type {number}
   */
  incrX = 0;

  /**
   * Incrément de position Y actuel.
   * @type {number}
   */
  incrY = 0;

  /**
   * Moitié de la largeur du container.
   * @type {number}
   */
  halfX = 0;

  /**
   * Moitié de la hauteur du container.
   * @type {number}
   */
  halfY = 0;

  /**
   * Fonction wrapper pour la position X (pour le wrapping infini).
   * @type {Function}
   */
  wrapX = null;

  /**
   * Fonction wrapper pour la position Y (pour le wrapping infini).
   * @type {Function}
   */
  wrapY = null;

  /**
   * Fonction d'animation rapide pour la position X.
   * @type {Function|null}
   */
  xTo = null;

  /**
   * Fonction d'animation rapide pour la position Y.
   * @type {Function|null}
   */
  yTo = null;

  /**
   * Animation GSAP en cours pour l'axe X.
   * @type {gsap.core.Tween|null}
   */
  xAnimation = null;

  /**
   * Animation GSAP en cours pour l'axe Y.
   * @type {gsap.core.Tween|null}
   */
  yAnimation = null;

  /**
   * Getter pour récupérer l'élément actuellement actif (agrandi).
   * @returns {ArchivesGalleryItem|undefined} Élément actif ou undefined.
   */
  get activeItem() {
    return this.items.find((item) => item.isActive && item.$options.clone === 0);
  }

  /**
   * Appelé lorsqu'un élément de la galerie change d'état (active/désactive).
   * Ajuste l'opacité de l'overlay en conséquence.
   * @returns {void}
   */
  onArchivesGalleryItemChange() {
    if (this.activeItem) {
      this.$refs.overlay.style.opacity = 1;
    } else {
      this.$refs.overlay.style.opacity = 0;
    }
  }

  /**
   * Getter pour récupérer le mode d'affichage actuel.
   * @returns {string} Mode d'affichage ('slider', 'grid', 'random', etc.).
   */
  get mode() {
    return this.$options.mode;
  }

  /**
   * Setter pour définir le mode d'affichage.
   * Réinitialise la galerie avec le nouveau mode.
   * @param {string} value Mode d'affichage ('slider', 'grid', 'random', etc.).
   * @returns {void}
   */
  set mode(value) {
    if (this.activeItem) {
      const activeItem = this.activeItem;
      activeItem.popOut();
      activeItem.$emit('change', false);
    }
    this.$refs.overlay.style.opacity = 0;
    this.availableZIndex = 10;
    Promise.all(this.items.map((item) => item.leave())).then(async () => {
      this.xAnimation?.pause();
      this.yAnimation?.pause();

      this.$options.mode = value;

      await nextFrame();

      this.resized();

      this.incrX = -this.halfX / 2;
      this.incrY = 0;

      gsap.set(this.container, {
        x: this.incrX,
        y: this.incrY,
        force3D: true,
        onComplete: () => {
          this.$emit('mode', value);
        },
      });
    });
  }

  /**
   * Getter pour récupérer l'élément container de la galerie.
   * @returns {HTMLElement} Élément container.
   */
  get container() {
    return this.$refs.container;
  }
}
